import type { GetQuestionnaireInvitesResponseRow } from '@risksmart-app/drizzle/src/types';
import type { GetQuestionnaireInvitesQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetQuestionnaireInvitesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

type UseGetQuestionnaireInvitesArgs = {
  thirdPartyId: string;
};

// The legacy query types `parent`, `thirdParty` and `questionnaireTemplateVersion`
// as NON-null (Hasura's FK metadata), but the tRPC row types the joins as
// nullable by default. Reconcile by dropping any invite whose required joins
// didn't resolve — in practice the FKs are NOT NULL so every row has them, and
// excluding an impossible row is behaviour-safe. Timestamps are real `Date`
// instances (superjson) on the tRPC side and `string` on the GraphQL side, so
// they are stringified here.
const toGraphQLRow = (
  row: GetQuestionnaireInvitesResponseRow
): GetQuestionnaireInvitesQuery['questionnaire_invite'][number] | null => {
  const { parent, thirdParty, questionnaireTemplateVersion } = row;
  if (!parent || !thirdParty || !questionnaireTemplateVersion) {
    return null;
  }

  return {
    Id: row.Id,
    UserEmail: row.UserEmail,
    CreatedAtTimestamp: new Date(row.CreatedAtTimestamp).toISOString(),
    ModifiedAtTimestamp: new Date(row.ModifiedAtTimestamp).toISOString(),
    parent: {
      Id: parent.Id,
      Status: parent.Status,
      StartDate: parent.StartDate
        ? new Date(parent.StartDate).toISOString()
        : null,
      ExpiresAt: parent.ExpiresAt
        ? new Date(parent.ExpiresAt).toISOString()
        : null,
      ResponseData: parent.ResponseData,
    },
    thirdParty: {
      Id: thirdParty.Id,
      Title: thirdParty.Title,
    },
    createdByUser: row.createdByUser
      ? { FriendlyName: row.createdByUser.FriendlyName }
      : null,
    modifiedByUser: row.modifiedByUser
      ? { FriendlyName: row.modifiedByUser.FriendlyName }
      : null,
    questionnaireTemplateVersion: {
      Id: questionnaireTemplateVersion.Id,
      Version: questionnaireTemplateVersion.Version,
      parent: questionnaireTemplateVersion.parent
        ? { Title: questionnaireTemplateVersion.parent.Title }
        : null,
    },
  };
};

export const useGetQuestionnaireInvites = createQueryHook<
  UseGetQuestionnaireInvitesArgs,
  GetQuestionnaireInvitesResponseRow[],
  GetQuestionnaireInvitesQuery
>({
  trpcQueryOptions: (trpc, { thirdPartyId }) =>
    trpc.frontend.questionnaireInvite.questionnaireInvites.queryOptions({
      thirdPartyId,
    }),
  // Legacy query root is the flat `questionnaire_invite` array; the tRPC
  // procedure returns the unwrapped rows.
  mapTrpcDataToGraphQL: (data) => ({
    questionnaire_invite: data
      .map(toGraphQLRow)
      .filter(
        (
          row
        ): row is GetQuestionnaireInvitesQuery['questionnaire_invite'][number] =>
          row !== null
      ),
  }),
  graphqlDocument: GetQuestionnaireInvitesDocument,
  graphqlVariables: ({ thirdPartyId }) => ({ thirdPartyId }),
  // The third-party questionnaires tab co-mounts other reads; cache-first keeps
  // the read shared and avoids the no-cache console.warn promoted to FATAL in
  // packages/web vitest.
  graphqlFetchPolicy: 'cache-first',
});
